import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { SessionService } from '@/features/session';
import { DEFAULT_AGENT_ID } from '@/config';

// Query Keys - centralized for consistency
export const sessionKeys = {
    all: ['sessions'] as const,
    lists: () => [...sessionKeys.all, 'list'] as const,
    list: (projectId: string) => [...sessionKeys.lists(), projectId] as const,
    details: () => [...sessionKeys.all, 'detail'] as const,
    detail: (projectId: string, sessionId: string) => [...sessionKeys.details(), projectId, sessionId] as const,
};

/**
 * Hook to fetch all sessions for a project
 * Used by: SessionList
 */
export function useSessionsQuery(projectId: string | undefined) {
    return useQuery({
        queryKey: sessionKeys.list(projectId!),
        queryFn: async () => {
            const sessions = await SessionService.getProjectSessions(projectId!);
            return sessions;
        },
        enabled: !!projectId,
        staleTime: 30_000,
    });
}

/**
 * Hook to fetch a single session's details
 * Used by: SessionView
 */
export function useSessionQuery(projectId: string | undefined, sessionId: string | undefined) {
    return useQuery({
        queryKey: sessionKeys.detail(projectId!, sessionId!),
        queryFn: () => SessionService.getSessionDetail(projectId!, sessionId!),
        enabled: !!projectId && !!sessionId,
    });
}

/**
 * Hook to create a new session
 */
export function useCreateSession() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ projectId, name }: { projectId: string; name?: string }) =>
            SessionService.createSession({
                project_id: projectId,
                name,
                agent_id: DEFAULT_AGENT_ID
            }),
        onSuccess: (_, { projectId }) => {
            queryClient.invalidateQueries({ queryKey: sessionKeys.list(projectId) });
        },
    });
}

/**
 * Hook to delete a session
 */
export function useDeleteSession() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ projectId, sessionId }: { projectId: string; sessionId: string }) =>
            SessionService.deleteSession(projectId, sessionId),
        onSuccess: (_, { projectId }) => {
            queryClient.invalidateQueries({ queryKey: sessionKeys.list(projectId) });
        },
    });
}

/**
 * Hook to update a session
 */
export function useUpdateSession() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ projectId, sessionId, data }: { projectId: string; sessionId: string; data: { name?: string; table_scope?: string[] | null; is_private?: boolean } }) =>
            SessionService.updateSession(projectId, sessionId, data),
        onSuccess: (_, { projectId, sessionId }) => {
            queryClient.invalidateQueries({ queryKey: sessionKeys.detail(projectId, sessionId) });
            queryClient.invalidateQueries({ queryKey: sessionKeys.list(projectId) });
        },
    });
}

/**
 * Hook to add a table to the session's table scope
 */
export function useAddTableToScope() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ projectId, sessionId, tableName }: { projectId: string; sessionId: string; tableName: string }) =>
            SessionService.addTableToSessionScope(projectId, sessionId, tableName),
        onSuccess: (_, { projectId, sessionId }) => {
            queryClient.invalidateQueries({ queryKey: sessionKeys.detail(projectId, sessionId) });
            queryClient.invalidateQueries({ queryKey: sessionKeys.list(projectId) });
        },
    });
}

/**
 * Hook to fork (bifurcate) a session into a new one.
 * Navigates to the new session after creation.
 */
export function useForkSession() {
    const queryClient = useQueryClient();
    const navigate = useNavigate();

    return useMutation({
        mutationFn: ({
            projectId,
            sessionId,
            upToMessageId,
            name,
        }: {
            projectId: string;
            sessionId: string;
            upToMessageId?: number;
            name?: string;
        }) =>
            SessionService.forkSession(projectId, sessionId, {
                up_to_message_id: upToMessageId,
                name,
            }),
        onSuccess: (newSession, { projectId }) => {
            queryClient.invalidateQueries({ queryKey: sessionKeys.list(projectId) });
            navigate(`/dashboard/project/${projectId}/session/${newSession.id}`);
        },
    });
}

/**
 * Hook to update the entire table scope of a session (or reset to null)
 */
export function useUpdateSessionTableScope() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ projectId, sessionId, tableScope }: { projectId: string; sessionId: string; tableScope: string[] | null }) =>
            SessionService.updateSessionTableScope(projectId, sessionId, tableScope),
        onSuccess: (_, { projectId, sessionId }) => {
            queryClient.invalidateQueries({ queryKey: sessionKeys.detail(projectId, sessionId) });
            queryClient.invalidateQueries({ queryKey: sessionKeys.list(projectId) });
        },
    });
}
